'use client';
import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import ShipmentCalcForm from './shipment-calc-form';
import { ENDPOINTS } from '@/config/endpoints';
import QUOTESFORMPOPUP from './../../component/shared/shipment-calculator/quotesFormpPopup'; // Adjust path as needed
import Toast from '../../component/shared/shipment-calculator/Toast';
import { pushCleverTapEvent } from '@/component/shared/pushCleverTapEvent';

export default function ShipmentCalc() {
  const router = useRouter();
  const defaultFormData = {
    shipmentDate: null,
    shipmentType: 'export',
    originCountry: '',
    originCountryLabel: '',
    originPort: '',
    originPortLabel: '',
    destinationCountry: '',
    destinationCountryLabel: '',
    destinationPort: '',
    destinationPortLabel: '',
    commodity: '',
    commodityLabel: '',
    containerType: '',
    containerTypeLabel: '',
    unit: '',
    length: '',
    width: '',
    height: '',
    dimensionUnit: 'CM',
    grossWeight: '',
    grossWeightUnit: 'KG',
  };

  const [quoteFlag, setQuoteFlag] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedFlag = localStorage.getItem('quoteFlag');
      return storedFlag ? JSON.parse(storedFlag) : true;
    }
    return true;
  });
  const [openQuotesPopup, setOpenQuotesPopup] = useState(false);
  const [loadType, setLoadType] = useState('FTL');
  const [currentForm, setCurrentForm] = useState(1);
  const [formData, setFormData] = useState(defaultFormData);
  const [loader, setLoader] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });

  const formRef = useRef();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedData = sessionStorage.getItem('shipmentFormData');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (parsedData.shipmentDate) {
            parsedData.shipmentDate = new Date(parsedData.shipmentDate);
            if (isNaN(parsedData.shipmentDate)) {
              parsedData.shipmentDate = null;
            }
          }
          setFormData(parsedData);
        } catch (error) {
          console.error('Failed to parse saved form data:', error);
          setFormData(defaultFormData);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem('shipmentFormData', JSON.stringify(formData));
      } catch (error) {
        console.error('Failed to save to sessionStorage:', error);
        setToast({
          message: 'Unable to save form data due to storage limits.',
          type: 'error',
        });
      }
    }
  }, [formData]);

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchQuotesData = async () => {
    console.log('Fetching quotes data...');
    console.log('tnc_shipment_typeValue:', formData.shipmentType);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('quotesData');
    }
    let apiUrl = '';
    let payload = {};
    const tnc_shipment_typeValue = formData.shipmentType;
    const formattedPickupDate = formData.shipmentDate
      ? formatDateToYYYYMMDD(formData.shipmentDate)
      : undefined;

    if (loadType === 'LTL') {
      apiUrl = ENDPOINTS.LCL_QUOTATION;
      payload = {
        additional_services: {
          aes: false,
          ams: false,
          destination_truck_services: {
            construction_site_fee: false,
            lift_gate_service: false,
            residential_service: false,
            trade_show_fee: false,
          },
          export_custom_clearance: false,
          freight_insurance: false,
          import_custom_clearance: false,
          insurance_coverage_limit: '200',
          non_stackable: false,
          origin_truck_services: {
            construction_site_fee: false,
            lift_gate_service: false,
            residential_service: false,
            trade_show_fee: false,
          },
          palletization: false,
        },
        destination_country: formData.destinationCountry,
        destination_locode: formData.destinationPort,
        destination_zipcode: '',
        dimensions: [
          {
            height: parseFloat(formData.height) || 0,
            length: parseFloat(formData.length) || 0,
            measurement_unit: formData.dimensionUnit,
            measurement_weight: formData.grossWeightUnit,
            weight: parseFloat(formData.grossWeight) || 0,
            width: parseFloat(formData.width) || 0,
          },
        ],
        item_description: `${formData.commodity} : ${formData.commodityLabel}`,
        no_of_unit: parseInt(formData.unit) || 1,
        origin_country: formData.originCountry,
        origin_locode: formData.originPort,
        origin_zipcode: '',
        packaging_type: 'Box',
        ready_for_pickup_date: formattedPickupDate,
        shipment_type: 'P2P',
        tnc_shipment_type: tnc_shipment_typeValue,
        volume: '0.0013',
        volume_unit: 'CBM',
        weight: parseFloat(formData.grossWeight) || 0,
        weight_unit: formData.grossWeightUnit,
      };
    } else if (loadType === 'FTL') {
      const containerSize = formData.containerTypeLabel.match(/^\d+/)?.[0] || '40';
      apiUrl = ENDPOINTS.FCL_QUOTATION;
      payload = {
        cargo_type: 'FCL',
        destination_country: formData.destinationCountry,
        destination_locode: formData.destinationPort,
        export_custom_clearance: false,
        freight_insurance: true,
        import_custom_clearance: false,
        origin_country: formData.originCountry,
        origin_locode: formData.originPort,
        quotation_load: [
          {
            container_size: containerSize,
            container_type: formData.containerType,
            item_description: `${formData.commodity} : ${formData.commodityLabel}`,
            measurement_weight: formData.grossWeightUnit,
            no_of_unit: parseInt(formData.unit) || 1,
            weight: formData.grossWeight || '0',
          },
        ],
        ready_for_pickup_date: formattedPickupDate,
        shipment_total_weight_unit: formData.grossWeightUnit,
        shipment_type: 'P2P',
        tnc_shipment_type: tnc_shipment_typeValue,
      };
    }

    setLoader(true);
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!result?.data?.message) {
        setToast({
          message: 'Data fetched successfully!',
          type: 'success',
        });
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('quotesData', JSON.stringify(result));
        }
        setLoader(false);
        router.push('/shipment-calculator/quotes');
      } else {
        setLoader(false);
        if (result?.data?.message === 'No results found with these search parameters') {
          console.log('Toast: No quotes found. Redirecting...');
          setToast({
            message: 'No quotes found. Redirecting...',
            type: 'error',
          });
          setTimeout(() => {
            router.push('/shipment-calculator/quotes');
          }, 2000); // 2 second delay so toast is visible
        } else if (result?.data?.message === 'Got some error. Please contact admin.') {
          console.log('Toast:', result?.data?.message);
          setToast({
            message: result?.data?.message,
            type: 'error',
          });
        } else {
          console.log('Toast:', (result?.data?.message + ' | Check All The Field Details And Confirm Them') || 'Failed to fetch data.');
          setToast({
            message: (result?.data?.message + ' | Check All The Field Details And Confirm Them') || 'Failed to fetch data.',
            type: 'error',
          });
        }
      }
    } catch (error) {
      setLoader(false);
      setToast({
        message: 'An unexpected error occurred. Please try again.',
        type: 'error',
      });
      console.error('Error during quote submission:', error);
    } finally {
      setTimeout(() => setToast({ message: '', type: '' }), 5000);
    }
  };

  const handleContinue = async () => {
    if (formRef.current) {
      const isValid = await formRef.current.validateCurrentStep();
      if (!isValid) return;

      if (currentForm === 1) {
        setCurrentForm(2);
      } else {
        if (quoteFlag) {
          setOpenQuotesPopup(true);
         if(loadType === 'FTL'){
          pushCleverTapEvent("Shipping_Calculator_Form_Search_FCL", { "PageName": 'Shipping_Calculator'})
          } else if (loadType === 'LTL') {
            pushCleverTapEvent("Shipping_Calculator_Form_Search_LCL", { "PageName": 'Shipping_Calculator'})
          }
        } else {
          await fetchQuotesData();
        }
      }
    }
  };

  const handleBack = () => {
    if (currentForm === 2) setCurrentForm(1);
  };

  const handleReset = () => {
    // setFormData(defaultFormData);
    setFormData({
      ...defaultFormData,
      shipmentType: formData.shipmentType // Preserve shipmentType
    });
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('shipmentFormData');
    }
    // Reset form errors if ref is available
    if (formRef.current && formRef.current.resetErrors) {
      formRef.current.resetErrors();
    }
    // Reset to first step
    setCurrentForm(1);
    setToast({
      message: 'Form has been reset.',
      type: 'success',
    });
    setTimeout(() => setToast({ message: '', type: '' }), 3000);
  };

  return (
    <div className="sm:pt-[100px] pt-[110px] shipBackground bg-gray-900 text-white">
      <div className="container flex justify-between sm:container py-5 lg:px-3 h-full xm:px-2 px-3 mx-auto items-center sm:flex-col">
        <div className="mb-6 lg:mb-0 max-w-xl">
          <h2 className="font-semibold text-2xl mb-2 text-yellow-400">
            Simplify international shipping with our online freight marketplace
          </h2>
          <h1 className="text-3xl font-bold leading-tight">
            search, compare, book, & finance <br />
            your import/export shipments <br />
            effortlessly <br />
            <span className="text-white">in a few clicks.</span>
          </h1>
          <p className="mt-4 text-xl text-white">
            Global trade, made easy with the leading freight platform.
          </p>
        </div>

        <div className="bg-white p-[20px] rounded-lg shadow-md w-full max-w-[550px] md:min-w-[540px] lg:min-w-[540px] text-baseBlack">
          <h3 className="relative text-xl flex justify-between font-semibold mb-4 text-center w-full text-baseBlack">
            {currentForm === 2 && (
              <button
                type="button"
                onClick={handleBack}
                className="text-gray-600 hover:text-gray-900"
                aria-label="Back"
              >
                <svg
                  className="w-4 text-grayText backArrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7.82843 10.9999H20V12.9999H7.82843L13.1924 18.3638L11.7782 19.778L4 11.9999L11.7782 4.22168L13.1924 5.63589L7.82843 10.9999Z"></path>
                </svg>
              </button>
            )}
            Calculate Shipping Price
            {/* {' '} */}
            <span className="text-sm text-gray-400 text-grayText">
              ({currentForm}/2)
            </span>
          </h3>

          <div className="mb-4 mt-4 flex items-center sm:flex-col">
            <label className="font-medium block pr-10 text-sm text-grayText sm:pr-0 sm:w-full sm:mb-2">Load Type:</label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  checked={loadType === 'FTL'}
                  onChange={() => {setLoadType('FTL');
                     pushCleverTapEvent("Shipping_Calculator_FCL_Tab", { "PageName": 'Shipping_Calculator'});
                  }}
                  name="loadType"
                />
                <span className="text-gray-700 text-sm">Full Truck Load (FTL)</span>
              </label>
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  checked={loadType === 'LTL'}
                  onChange={() => {setLoadType('LTL');
                    pushCleverTapEvent("Shipping_Calculator_LCL_Tab", { "PageName": 'Shipping_Calculator'});
                  }}
                  name="loadType"
                />
                <span className="text-gray-700 text-sm">Less-Than-Truckload (LTL)</span>
              </label>
            </div>
          </div>

          <ShipmentCalcForm
            ref={formRef}
            currentForm={currentForm}
            formData={formData}
            setFormData={setFormData}
            loadType={loadType}
          />

          {toast.message && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ message: '', type: '' })}
              duration={5000}
            />
          )}

          {loader && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin"></div>
            </div>
          )}

          {openQuotesPopup && (
            <div onClick={(event) => event.stopPropagation()} className="cursor-default">
              <div className="fixed bg-[rgba(0,0,0,0.6)] inset-0 left-0 top-0 z-[1040] transition h-[100vh] w-[100vw]"></div>
              <QUOTESFORMPOPUP
                setOpenQuotesPopup={setOpenQuotesPopup}
                setQuoteFlag={setQuoteFlag}
                fetchQuotesData={fetchQuotesData}
              />
            </div>
          )}

          <div className="flex justify-center pt-1 space-x-4 sm:space-x-1">
            <button
              onClick={handleReset}
              className="w-fit bg-[#ffffff] bg-gray-500 border-[1px] border-[#0363C4] text-[#288BED] font-medium h-9 text-sm px-6 sm:px-2 rounded hover:bg-gray-600 whitespace-nowrap"
            >
              Reset Form
            </button>
            <button
              onClick={handleContinue}
              disabled={loader}
              className={`w-fit bg-gradient-to-r font-medium from-[#022B5C] h-9 to-[#0363C4] border border-[#022B5C] text-white text-sm px-12 sm:px-4 rounded ${loader ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              {currentForm === 1 ? 'Continue' : 'Search Price'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}